/* eslint-disable no-console */
import React, { Fragment, createElement } from 'react';
import { useTranslation } from 'react-i18next';

interface TranslationArgs {
  id: string;
  defaultMessage?: string;
}

/**
 * Custom hook to keep react-intl syntax and replace the logic with react-i18next.
 * Provides a translation function `t` that can be used to translate messages.
 *
 * @returns An object containing the translation function `t`.
 */
export const useIntl = () => {
  const { t: translate } = useTranslation();

  return {
    /**
     * Translates the given message using the provided translation key and values.
     *
     * @param id - The translation key.
     * @param defaultMessage - The default message to use if the translation key is not found.
     * @param values - Optional values to replace placeholders in the translated message.
     * @returns The translated message.
     */
    t: ({ id, defaultMessage = '' }: TranslationArgs, values?: any): string => {
      const template = translate(id, defaultMessage);
      if (!template) {
        console.error(`Missing translation for key: ${id}`);
      }
      const tokens = template.split(/(\{[^}]+\})/);
      let allStrings = true;

      const formattedValues = tokens.map((token, index) => {
        const match = token.match(/^\{(.+)\}$/);
        if (match) {
          const replacement = values && values[match[1]];
          if (React.isValidElement(replacement)) {
            allStrings = false;
            return createElement(Fragment, { key: index }, replacement);
          } else {
            return replacement !== undefined ? replacement : token;
          }
        }
        return token;
      });

      return allStrings
        ? formattedValues.join('')
        : (createElement(Fragment, null, ...formattedValues) as unknown as string);
    },
  };
};
